import debounce from 'lodash/debounce';
import previewCohortGql from '../gql/previewCohort.gql';
import saveCohortGql from '../gql/saveCohort.gql';
import modifyCohortGql from '../gql/modifyCohort.gql';
import deleteCohortGql from '../gql/deleteCohort.gql';
import cloneCohortGql from '../gql/cloneCohort.gql';
import cohortDetailGql from '../gql/cohortDetail.gql';
import listCohortsGql from '../gql/listCohorts.gql';
import checkCohortNameGql from '../gql/checkCohortName.gql';
import gql from 'graphql-tag';

// ====== MELANOMA 字段字典 ======
const MELANOMA_DICT = {
  patient_info: {
    label: '患者基本信息',
    fields: {
      gender: { label: '性别', type: 'enum', options: ['男', '女'], ops: ['=', 'IN'] },
      age: { label: '年龄', type: 'number', ops: ['>', '<', 'BETWEEN', '='] },
      skin_color: { label: '肤色', type: 'enum', options: ['黄色', '白色', '黑色', '棕色'], ops: ['=', 'IN'] },
      race: { label: '民族', type: 'string', ops: ['=', 'IN'] },
      profession: { label: '职业', type: 'string', ops: ['=', 'CONTAINS'] },
      address: { label: '地址', type: 'string', ops: ['CONTAINS'] },
      label: { label: '标签', type: 'string', ops: ['CONTAINS'] },
      skin_type: { label: '皮肤类型', type: 'enum', options: ['I型', 'II型', 'III型', 'IV型', 'V型', 'VI型'], ops: ['=', 'IN'] }
    }
  },
  basic_info: {
    label: '临床基础',
    fields: {
      visit_date: { label: '接诊时间', type: 'date', ops: ['BETWEEN', '>', '<', '='] },
      ajcc_stage: {
        label: 'AJCC8临床分期',
        type: 'enum',
        options: ['0期', 'I期', 'IA期', 'IB期', 'II期', 'IIA期', 'IIB期', 'IIC期', 'III期', 'IIIA期', 'IIIB期', 'IIIC期', 'IV期'],
        ops: ['IN', '=']
      },
      primary_site: {
        label: '原发部位',
        type: 'enum',
        options: ['肢端', '黏膜', '头颈', '躯干', '四肢', '不明'],
        ops: ['IN', '=']
      },
      date_of_confirmed_diagnosis: { label: '确诊年月', type: 'string', ops: ['=', 'BETWEEN'] }
    }
  },
  pathology: {
    label: '病理特征',
    fields: {
      breslow_thickness: { label: 'Breslow厚度(mm)', type: 'number', ops: ['>', '<', 'BETWEEN', '='] },
      has_ulcer: { label: '表面溃破', type: 'enum', options: ['有', '无'], ops: ['='] }
    }
  },
  molecular: {
    label: '基因与分子指标',
    fields: {
      braf_mut: { label: 'B-raf基因状态', type: 'enum', options: ['阳性', '阴性', '野生型', '不详'], ops: ['=', 'IN', 'CONTAINS'] },
      ki67: { label: 'Ki-67增殖指数', type: 'string', ops: ['CONTAINS'] }
    }
  }
};

// 疾病字典路由
const DISEASE_DICTS = {
  MELANOMA: MELANOMA_DICT
};

export default {
  data() {
    return {
      currentDisease: 'MELANOMA',
      queryTree: { type: 'group', logic: 'AND', children: [] },
      previewCount: 0,
      isPreviewLoading: false,
      cohortName: '',
      cohortDescription: '',
      cohortStatus: '',
      cohortPatientCount: 0,
      cohortCreateTime: '',
      cohortUpdateTime: '',
      currentCohortId: null,
      dictSearchKeyword: '',
      // 队列列表
      cohortList: [],
      cohortListTotal: 0,
      cohortListPage: 1,
      cohortListPageSize: 10,
      cohortListLoading: false,
      listKeyword: ''
    };
  },
  computed: {
    currentDict() {
      return DISEASE_DICTS[this.currentDisease] || {};
    },
    filteredDictGroups() {
      const dict = this.currentDict;
      const keyword = (this.dictSearchKeyword || '').trim().toLowerCase();
      const groups = Object.keys(dict).map(groupKey => ({
        key: groupKey,
        label: dict[groupKey].label,
        fields: Object.keys(dict[groupKey].fields)
          .filter(fieldKey => {
            if (!keyword) return true;
            const label = dict[groupKey].fields[fieldKey].label.toLowerCase();
            return label.includes(keyword) || fieldKey.toLowerCase().includes(keyword);
          })
          .map(fieldKey => ({
            key: fieldKey,
            label: dict[groupKey].fields[fieldKey].label
          }))
      }));
      return groups.filter(g => g.fields.length > 0);
    }
  },
  watch: {
    queryTree: {
      deep: true,
      handler() {
        if (this.validateTree(this.queryTree)) {
          this.executePreview();
        } else {
          this.previewCount = 0;
        }
      }
    }
  },
  created() {
    this.executePreview = debounce(this._executePreview, 500);
    this.fetchCohortList();
  },
  methods: {
    // ====== 字典相关 ======
    getFieldMeta(fieldKey) {
      const dict = this.currentDict;
      for (const groupKey in dict) {
        if (dict[groupKey].fields[fieldKey]) {
          return dict[groupKey].fields[fieldKey];
        }
      }
      return null;
    },

    onDiseaseChange() {
      this.queryTree = { type: 'group', logic: 'AND', children: [] };
      this.previewCount = 0;
      this.currentCohortId = null;
      this.cohortName = '';
      this.cohortDescription = '';
      this.cohortListPage = 1;
      this.fetchCohortList();
    },

    // ====== 条件树操作 ======
    addCondition(parentGroup) {
      parentGroup.children.push({
        type: 'condition',
        field: '',
        operator: '',
        value: null
      });
    },

    addGroup(parentGroup) {
      parentGroup.children.push({
        type: 'group',
        logic: 'AND',
        children: []
      });
    },

    removeNode({ parentGroup, index }) {
      if (parentGroup && index >= 0 && index < parentGroup.children.length) {
        parentGroup.children.splice(index, 1);
      }
    },

    addConditionFromDict(fieldKey) {
      const meta = this.getFieldMeta(fieldKey);
      if (!meta) return;
      const newCondition = {
        type: 'condition',
        field: fieldKey,
        operator: meta.ops[0] || '',
        value: this._initValueForField(meta, meta.ops[0])
      };
      this.queryTree.children.push(newCondition);
    },

    onFieldChange(item, fieldKey) {
      // 由 CohortFilterItem 内部处理 operator/value 重置
    },

    onOperatorChange(item, op) {
      // 由 CohortFilterItem 内部处理 value 重置
    },

    _initValueForField(meta, operator) {
      if (operator === 'IN') return [];
      if (operator === 'BETWEEN') {
        return [null, null];
      }
      if (meta.type === 'number') return null;
      return '';
    },

    // ====== 数据管道 ======
    validateTree(node) {
      if (!node || !node.type) return false;
      if (node.type === 'group') {
        if (!node.children || node.children.length === 0) return false;
        return node.children.every(child => this.validateTree(child));
      }
      if (node.type === 'condition') {
        return !!(node.field && node.operator && this._isValidValue(node));
      }
      return false;
    },

    _isValidValue(item) {
      if (item.value === null || item.value === undefined) return false;
      if (Array.isArray(item.value)) {
        if (item.value.length === 0) return false;
        if (item.operator === 'BETWEEN' && item.value.some(v => v === null || v === undefined || v === '')) return false;
      }
      if (typeof item.value === 'string' && item.value.trim() === '') return false;
      return true;
    },

    cleanEmptyNodes(node) {
      if (!node) return null;
      if (node.type === 'group') {
        node.children = node.children
          .map(child => this.cleanEmptyNodes(child))
          .filter(child => child !== null);
        return node.children.length > 0 ? node : null;
      }
      if (node.type === 'condition') {
        return (node.field && node.operator && this._isValidValue(node)) ? node : null;
      }
      return null;
    },

    _executePreview() {
      if (!this.validateTree(this.queryTree)) {
        this.previewCount = 0;
        return;
      }
      this.isPreviewLoading = true;
      const cleanedTree = JSON.parse(JSON.stringify(this.queryTree));
      const cleaned = this.cleanEmptyNodes(cleanedTree);
      if (!cleaned) {
        this.previewCount = 0;
        this.isPreviewLoading = false;
        return;
      }
      this.$apollo.query({
        query: previewCohortGql,
        variables: {
          disease: this.currentDisease,
          conditions: cleaned
        },
        fetchPolicy: 'no-cache'
      }).then(res => {
        this.previewCount = res.data.previewCohort || 0;
      }).catch(err => {
        console.error('Preview error:', err);
        this.previewCount = 0;
      }).finally(() => {
        this.isPreviewLoading = false;
      });
    },

    // 点击"查看筛选结果"按钮：已保存则刷新患者列表，未保存则提示先保存
    handlePreviewViewResult() {
      if (this.currentCohortId) {
        this.currentPagePatientList = 1;
        this.getCohortPatients();
        this.$message.success('已刷新筛选结果');
      } else {
        this.$message.warning('请先保存队列后再查看筛选结果');
      }
    },

    // ====== 队列 CRUD ======
    // asNew=false 且 currentCohortId 存在时，调用 modifyCohort 更新已有队列
    // asNew=true 或 currentCohortId 不存在时，调用 saveCohort 创建新队列
    async handleSaveCohort(asNew = false) {
      const cleanedTree = JSON.parse(JSON.stringify(this.queryTree));
      const cleaned = this.cleanEmptyNodes(cleanedTree);
      if (!cleaned) {
        this.$message.warning('请至少添加一个有效的筛选条件');
        return;
      }

      if (this.currentCohortId && !asNew) {
        // 更新已有队列
        try {
          const res = await this.$apollo.mutate({
            mutation: modifyCohortGql,
            variables: {
              id: this.currentCohortId,
              name: this.cohortName,
              description: this.cohortDescription,
              conditions: cleaned
            }
          });
          this.cohortName = res.data.modifyCohort.name;
          this.$message.success('队列更新成功');
          this.fetchCohortList();
        } catch (err) {
          this.$message.error('更新失败: ' + (err.message || '未知错误'));
        }
      } else {
        // 创建新队列，需确认名称
        let name = '';
        try {
          const { value } = await this.$prompt('请输入队列名称', '保存为新队列', {
            inputValue: asNew ? '' : (this.cohortName || ''),
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            inputValidator: async (val) => {
              if (!val || !val.trim()) return '队列名称不能为空';
              // 前端实时校验重名
              try {
                const checkRes = await this.$apollo.query({
                  query: checkCohortNameGql,
                  variables: {
                    disease: this.currentDisease,
                    name: val.trim(),
                    excludeId: asNew ? null : this.currentCohortId
                  },
                  fetchPolicy: 'no-cache'
                });
                if (!checkRes.data.checkCohortName) {
                  return '该队列名称在同一团队内已存在，请更换名称';
                }
              } catch (e) {
                // 校验接口异常时不阻断，由后端兜底
                console.warn('checkCohortName 接口异常:', e);
              }
              return true;
            }
          });
          name = value.trim();
        } catch {
          return; // 用户取消
        }

        try {
          const res = await this.$apollo.mutate({
            mutation: saveCohortGql,
            variables: {
              disease: this.currentDisease,
              name: name,
              description: this.cohortDescription,
              conditions: cleaned
            }
          });
          this.currentCohortId = res.data.saveCohort.id;
          this.cohortName = name;
          this.$message.success('队列保存成功');
          this.fetchCohortList();
        } catch (err) {
          this.$message.error('保存失败: ' + (err.message || '未知错误'));
        }
      }
    },

    async loadCohort(cohortId) {
      try {
        const res = await this.$apollo.query({
          query: cohortDetailGql,
          variables: { id: cohortId },
          fetchPolicy: 'no-cache'
        });
        const data = res.data.cohortDetail;
        this.currentCohortId = data.id;
        this.currentDisease = data.disease;
        this.cohortName = data.name;
        this.cohortDescription = data.description || '';
        this.cohortStatus = data.status || 'ACTIVE';
        this.cohortPatientCount = data.patient_count || 0;
        this.cohortCreateTime = data.create_time || '';
        this.cohortUpdateTime = data.update_time || '';
        this.queryTree = JSON.parse(JSON.stringify(data.conditions_json)); // 加载过滤器树
        this.currentPagePatientList = 1; // 切换队列时重置患者列表页码
        this.getCohortPatients(); // 加载队列后自动加载患者列表
      } catch (err) {
        this.$message.error('加载队列失败: ' + (err.message || '未知错误'));
      }
    },

    // ====== 队列列表 ======
    async fetchCohortList() {
      this.cohortListLoading = true;
      try {
        const res = await this.$apollo.query({
          query: listCohortsGql,
          variables: {
            disease: this.currentDisease,
            page_number: this.cohortListPage,
            page_size: this.cohortListPageSize
          },
          fetchPolicy: 'no-cache'
        });
        const { count, data } = res.data.listCohorts;
        this.cohortListTotal = count;
        // 前端关键字过滤（后端 listCohorts 不支持 name 模糊搜索）
        if (this.listKeyword) {
          const kw = this.listKeyword.trim().toLowerCase();
          this.cohortList = data.filter(c => c.name.toLowerCase().includes(kw));
        } else {
          this.cohortList = data;
        }
      } catch (err) {
        console.error('fetchCohortList error:', err);
        this.cohortList = [];
        this.cohortListTotal = 0;
      } finally {
        this.cohortListLoading = false;
      }
    },

    handleCohortListPageChange(page) {
      this.cohortListPage = page;
      this.fetchCohortList();
    },

    handleLoadCohort(cohort) {
      this.loadCohort(cohort.id);
    },

    handleNewCohort() {
      this.queryTree = { type: 'group', logic: 'AND', children: [] };
      this.previewCount = 0;
      this.currentCohortId = null;
      this.cohortName = '';
      this.cohortDescription = '';
      this.cohortStatus = '';
      this.cohortPatientCount = 0;
      this.cohortCreateTime = '';
      this.cohortUpdateTime = '';
      
      // 清空患者列表
      this.patients = [];
      this.currentPagePatientList = 1;
      this.currentPatient = null;
    },

    async handleToggleFreeze(cohort) {
      const newStatus = cohort.status === 'FROZEN' ? 'ACTIVE' : 'FROZEN';
      const actionText = newStatus === 'FROZEN' ? '冻结' : '解冻';
      try {
        await this.$confirm(`确定要${actionText}队列「${cohort.name}」吗？`, '确认操作', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
      } catch { return; }

      try {
        const res = await this.$apollo.mutate({
          mutation: gql`
            mutation updateCohortStatus($cohortId: Int!, $status: CohortStatus!) {
              updateCohortStatus(cohortId: $cohortId, status: $status)
            }
          `,
          variables: { cohortId: cohort.id, status: newStatus }
        });
        if (res.data.updateCohortStatus) {
          this.$message.success(`${actionText}成功`);
          this.fetchCohortList();
        }
      } catch (err) {
        this.$message.error(`${actionText}失败: ` + (err.message || '未知错误'));
      }
    },

    async handleDeleteCohort(cohort) {
      try {
        await this.$confirm(`确定要删除队列「${cohort.name}」吗？此操作不可恢复。`, '确认删除', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
      } catch { return; }

      try {
        const res = await this.$apollo.mutate({
          mutation: deleteCohortGql,
          variables: { id: cohort.id }
        });
        if (res.data.deleteCohort) {
          this.$message.success('删除成功');
          // 如果删除的是当前正在编辑的队列，清空编辑区
          if (this.currentCohortId === cohort.id) {
            this.handleNewCohort();
          }
          this.fetchCohortList();
        }
      } catch (err) {
        this.$message.error('删除失败: ' + (err.message || '未知错误'));
      }
    },

    async handleCloneCohort(cohort) {
      let name = '';
      try {
        const { value } = await this.$prompt('请输入新队列名称', '克隆队列', {
          inputValue: cohort.name + ' (副本)',
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputValidator: async (val) => {
            if (!val || !val.trim()) return '队列名称不能为空';
            try {
              const checkRes = await this.$apollo.query({
                query: checkCohortNameGql,
                variables: {
                  disease: cohort.disease || this.currentDisease,
                  name: val.trim()
                },
                fetchPolicy: 'no-cache'
              });
              if (!checkRes.data.checkCohortName) {
                return '该队列名称在同一团队内已存在，请更换名称';
              }
            } catch (e) {
              console.warn('checkCohortName 接口异常:', e);
            }
            return true;
          }
        });
        name = value.trim();
      } catch { return; }

      try {
        await this.$apollo.mutate({
          mutation: cloneCohortGql,
          variables: { id: cohort.id, name }
        });
        this.$message.success('克隆成功');
        this.fetchCohortList();
      } catch (err) {
        this.$message.error('克隆失败: ' + (err.message || '未知错误'));
      }
    },

    handleSearchCohorts() {
      this.cohortListPage = 1;
      this.fetchCohortList();
    },

    // dropdown 命令分发
    handleCohortCommand(command, cohort) {
      if (command === 'clone') this.handleCloneCohort(cohort);
      else if (command === 'delete') this.handleDeleteCohort(cohort);
    }
  }
};
